import React, { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  Typography
} from '@mui/material'
import { useQuery } from 'react-query'
import { TreeViewCheck } from 'src/components/uiElements/styledComponents/TreeViewCheck'
import { workUnitServices } from 'src/services/reservation/workUnit/WorkUnit.service'
import { TextInput, useTranslate, DateInput, DateTimeInput, BooleanInput, useNotify } from 'react-admin'
import ContentFilter from '@mui/icons-material/FilterList'
import isEqual from 'lodash/isEqual'
import { SplitByCharacterWithPosition } from 'src/helper/splitByCharacter'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'
import { countryServices } from 'src/services/reservation/country/country.service'
import { userServices } from 'src/services/reservation/user/user.service'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput.jsx'
import { productTypeServices } from 'src/services/reservation/product-type/product_type.service'
import { productServices } from 'src/services/reservation/product/product.service'
import { supplierServices } from 'src/services/reservation/supplier/supplier.service'
import { Close, HighlightOff, Search } from '@mui/icons-material'
import { tagServices } from 'src/services/reservation/tag/tag.service'
import { sourceServices } from 'src/services/reservation/source/source.service'
import get from 'lodash/get'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput.jsx'
import { reportingServices } from 'src/services/sales/reporting/reporting.service'
import { LoadingButton } from '@mui/lab'
import { ReportingContext } from './ReportingContext';
import { reservationServices } from 'src/services/sales/reservation/reservation.service';
import { MultiAutocompleteInput } from 'src/components/uiElements/fields/MultiAutocompleteInput';
import { paymentServices } from 'src/services/payment/Payment.service';

export const Filters = ({displayedFilters, setFilters, listFilters, setFilterValues}) => {
  const translate = useTranslate()
  
  const hasDisplayedFilters = !isEqual(displayedFilters, {})
  const [open, setOpen] = useState(false)
  const anchorEl = useRef()
  
  const showFilter = (filterName, value) => {
    setFilterValues((values) => ({
      ...values,
      [filterName]: value,
    }))
    setFilters((displayedCreditFilters) => ({
      ...displayedCreditFilters,
      [filterName]: true,
    }))
  }

  const handleClickButton = useCallback(
    (event) => {
      // This prevents ghost click.
      event.preventDefault()
      setOpen(true)
      anchorEl.current = event.currentTarget
    },
    [anchorEl, setOpen]
  )

  const handleRequestClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleShow = useCallback(
    ({ source, defaultValue }) => {
      showFilter(source, defaultValue === '' ? undefined : defaultValue)
      setOpen(false)
    },
    [showFilter, setOpen]
  )

  return (
    <Box>
      <Button
        className='add-filter'
        label='ra.action.add_filter'
        aria-haspopup='true'
        onClick={handleClickButton}
        size='small'
      >
        <ContentFilter /> {translate('ra.action.add_filter')}
      </Button>
      <Menu open={open} anchorEl={anchorEl.current} onClose={handleRequestClose}>
        {Array.isArray(listFilters) &&
          listFilters.map(
            (item, index) =>
              !displayedFilters?.[item.source] && (
                <MenuItem
                  onClick={() => {
                    setOpen(false)
                    handleShow({ source: item.source })
                  }}
                  key={index}
                >
                  {translate(`${item.name}`)}
                </MenuItem>
              )
          )}

        {hasDisplayedFilters && (
          <MenuItem onClick={() => setFilters((displayedCreditFilters) => (Object.entries(displayedCreditFilters).map(([key, _])=>{return {[key]:false}})))}>
            {translate('ra.action.remove_all_filters')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

const assignIdToValue = (objects, labelField) => {
  if (Array.isArray(objects)) {
    return objects.map((obj) => {
      const newObj = {
        id: obj?.id,
        fiscal_data_relation: obj?.fiscal_data_relation,
        label: obj?.label,
        token: obj?.token,
        fantasy_name: obj?.fantasy_name,
        short_name: obj?.short_name,
        long_name: obj?.long_name,
        value: obj?.value,
        children: obj?.children || null,
      }

      // Usar 'get' para acceder a propiedades anidadas
      newObj.label = labelField
        ?.map((field) => get(obj, field))
        .filter((elem) => elem)
        .join(' - ')
      if (newObj.id !== undefined) {
        newObj.value = String(newObj.id) + '__' + newObj.label
      }

      if (newObj.children && newObj.children.length > 0) {
        newObj.children = assignIdToValue(newObj.children, labelField)
      } else {
        newObj.children = null
      }

      return newObj
    })
  }
  return null
}

const getDefaultValueTreeView = (data, value) => {
  let valueAux = []
  data.forEach((elem) => {
    if (elem.id === value) {
      valueAux.push(elem.value)
    } else if (elem.children && elem.children.length > 0) {
      valueAux = valueAux.concat(getDefaultValueTreeView(elem.children, value))
    }
  })

  return valueAux
}

const filterTreeView = (data, value, labelField) => {
  let expanded = []

  if (value) {
    const dataAux = []
    data.forEach((elem) => {
      const [filteredChildren, expandedChildren] = elem.children
        ? filterTreeView(elem.children, value, labelField)
        : [[], []]
      if (filteredChildren.length > 0) {
        elem.children = filteredChildren
        dataAux.push(elem)
        expanded = expanded.concat([elem.value, expandedChildren]).flat()
      } else if (elem[labelField]?.toLowerCase()?.includes(value)) {
        dataAux.push(elem)
      }
    })

    return [dataAux, expanded]
  }

  return [data, expanded]
}

const getId = (value) => {
  if (value && value.length) {
    return value.map((value) => SplitByCharacterWithPosition(value, '__', 0)).join(',')
  }
}

const getLabel = (value, emptyText) => {
  if (value && value.length) {
    return value.map((value) => SplitByCharacterWithPosition(value, '__', 1)).join(', ')
  }

  return emptyText
}

const SelectArrayInputFn = ({
  queryName,
  label,
  optionsText = ['long_name'],
  optionValue,
  source,
  queryFn,
  ...props
}) => {
  const { setValue} = useFormContext()
  const [isSelectedAll, setIsSelectedAll] = useState(false)
  const showAllOption = (source === 'item__product_id__in' || source === 'product_type_id__in') ?
    true : false
  let options = useQuery(queryName, () => queryFn())
  if(queryName === 'bases-all' && options && options.data){
    options.data = options.data?.map(opt=>{return {name:opt}})
  }
  if(queryName === 'payment-type' && options && options.data){
    options.data = options.data?.map(opt=>{return {name:opt.long_name, id:opt.id}})
  }
  return options.isLoading ? (
    <Skeleton variant='rectangular' width={'270px'} height={'40px'} />
  ) : (
    <FormControl fullWidth className='select-array-input'>
      {label && <InputLabel>{label}</InputLabel>}
      <SelectInput
        source={source}
        options={options?.data || []}
        optionsText={optionsText}
        optionValue={optionValue}
        multiple
        showAllOption = {showAllOption}
        hideInclude = {showAllOption}
        onChange={(value)=>{
          if(showAllOption){
            if(value.target.value && value.target.value[0] === 'all'){
              if(isSelectedAll){
                setIsSelectedAll(false)
                setValue(source, [])
              } else {
                setIsSelectedAll(true)
                setValue(source, options.data.map(opt=>opt.id))
              }
              
            }
          }
        }}
        showSearch
        {...props}
      />
    </FormControl>
  )
}

export const TreeViewInput = ({
  queryName,
  fieldName,
  modalTitle,
  buttonText,
  isShow,
  labelField,
  queryFn,
  limitOptions = 30,
  filterValues
}) => {
  const options = useQuery(queryName, () => queryFn())
  const translate = useTranslate()
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState([])
  const [currentOption, setCurrentOption] = useState([])
  const [formatedOptions, setFormatedOptions] = useState([])
  const { setValue } = useFormContext()

  useEffect(() => {
    if (open) {
      setFormatedOptions(assignIdToValue(options.data.slice(0, limitOptions), labelField))
      setExpanded([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!options.isLoading) {
      const nodes = assignIdToValue(options.data.slice(0, limitOptions), labelField)
      const defaultValues = []
      filterValues[fieldName]?.split(',')?.forEach((elem) => {
        const id = parseInt(elem) || null
        if (id) {
          const defaultValue = getDefaultValueTreeView(nodes, id)
          defaultValues.push(defaultValue)
        }
      })
      setCurrentOption(defaultValues.flat())
      setFormatedOptions(nodes)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.isLoading])

  useEffect(() => {
    if (currentOption) {
      setValue(fieldName, getId(currentOption))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOption])

  const onSearch = (value) => {
    if (value.length >= 3) {
      const nodes = assignIdToValue(options.data, labelField)
      const [filteredNodes, expandedNodes] = filterTreeView(nodes, value.toLowerCase(), 'label')
      setFormatedOptions(filteredNodes)
      setExpanded(expandedNodes)
    } else {
      // Si la búsqueda es menos de 3 caracteres, muestra solo las opciones seleccionadas o resetea a la vista inicial
      setFormatedOptions(
        currentOption.length
          ? currentOption
          : assignIdToValue(options.data.slice(0, limitOptions), labelField)
      )
    }
  }

  const onCloseModal = () => {
    setOpen(false)
  }

  return (
    <>
      <Dialog open={open} maxWidth='md' fullWidth onClose={onCloseModal}>
        <DialogTitle display='flex' justifyContent='space-between'>
          {modalTitle}
          <IconButton size='small' onClick={onCloseModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size='small'
            placeholder={translate('ra.message.select_at_least', { num: 3 })}
            style={{ marginBottom: 24 }}
            onChange={(e) => e.target.value.length >= 3 && onSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          {formatedOptions.length >= limitOptions && (
            <Typography variant='caption' display='block' style={{ color: 'gray', marginLeft: 10 }}>
              vista previa de resultados ({limitOptions}).
            </Typography>
          )}
          <TreeViewCheck
            checked={currentOption}
            setChecked={setCurrentOption}
            nodes={formatedOptions}
            setExpanded={setExpanded}
            expanded={expanded}
            onlyOne={false}
            noCascade={false}
            activeHistorical={true}
            allNodes={assignIdToValue(options.data, labelField)}
          />
        </DialogContent>
      </Dialog>

      <Box width={'100%'}>
        {options.isLoading ? (
          <Skeleton variant='rectangular' width={'250px'} height={'40px'} />
        ) : (
          <Button
            sx={{
              width: '100%',
              height: '40px',
            }}
            disabled={isShow}
            onClick={() => setOpen(true)}
            variant='outlined'
          >
            <span style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflowX: 'hidden' }}>
              {getLabel(currentOption, buttonText)}
            </span>
          </Button>
        )}
      </Box>
    </>
  )
}

const RemoveFilterField = ({ isShow, children, prop, fields, width = null, maxWidth = null, minWidth = null, displayedFilters = {}, setFilters }) => {
  const { setValue, getValues } = useFormContext()

  const onClickButton = () => {
    const copy = { ...displayedFilters }
    delete copy[prop]

    if (Array.isArray(fields) && fields.length > 0) {
      fields.forEach((field) => setValue(field, undefined))

      let values = getValues()
      if (values.business_unit__country_id__in) {
        values.business_unit__country_id__in = values.business_unit__country_id__in.map((elem) => elem.id)
      }
      setFilters((filters)=>{return {...filters, [prop]:false}})
    }
  }

  return (
    <Box
      display={'flex'}
      alignItems={'flex-start'}
      marginRight={5}
      width={width}
      maxWidth={maxWidth}
      minWidth={minWidth}
    >
      {!isShow && <Box width={'34px'} paddingTop={1}>
        <IconButton size='small' onClick={onClickButton}>
          <HighlightOff />
        </IconButton>
      </Box>}
      <Box width={'calc(100% - 34px)'} display={'flex'} minHeight={'60px'} height={'auto'}>
        {children}
      </Box>
    </Box>
  )
}

const CountryInput = ({ label, source, isShow }) => {
  const [loadingCountryList, setLoadingCountryList] = useState(false)
  const [countryList, setCountryList] = useState([])

  const getCountry = async (long_name) => {
    try {
      setLoadingCountryList(true)
      const allCountries = await countryServices.getByAll({ long_name: long_name })
      setCountryList(allCountries)
      setLoadingCountryList(false)
    } catch (error) {
      setCountryList([])
      setLoadingCountryList(false)
    }
  }

  return (
    <AutocompleteInput
      source={source}
      label={label}
      disabled={isShow}
      options={countryList}
      loading={loadingCountryList}
      optionsText={['long_name']}
      optionValue='id'
      onSearch={getCountry}
      resetOnLoad={false}
      style={{ width: '100%' }}
      positionOptionsText={0}
      multiple
    />
  )
}

export const PostFilterFormReservation = ({setFormError,isShow, displayedFilters = {}, filterValues, setFilters, initialFields, reportToEdit, setOpenEditDialog}) => {
  const translate = useTranslate()
  const form = useForm()
  const notify = useNotify()
  const navigate = useNavigate()
  const [editLoading, setEditLoading] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const {setActivateRefresh, setRefresh} = useContext(ReportingContext);
  const [usersList, setUserList] = useState([])
  const [usersLoading, setLoadingUserList] = useState(false)
  const [openSellerDialog, setOpenSellerDialog] = useState(false)
  const [sellers, setSellers] = useState([])
  const [openCommercialResponsibleDialog, setOpenCommercialResponsibleDialog] = useState(false)
  const [commercialResponsible, setCommercialResponsible] = useState([])
  const [openManagerResponsibleDialog, setOpenManagerResponsibleDialog] = useState(false)
  const [managerResponsible, setManagerResponsible] = useState([])

  useEffect(()=>{
    if(filterValues['business_unit__country_id__in']?.length > 0){
      const fetchCountries = async () => {
        let auxCountries = []
        for(let country of filterValues['business_unit__country_id__in']){
          const countryObject = await countryServices.getById(country)
          auxCountries.push(countryObject)
        }
        form.setValue('business_unit__country_id__in', auxCountries)
      }
      fetchCountries()
    }
  },[filterValues['business_unit__country_id__in']])

  useEffect(()=>{
    if(!displayedFilters['user__hash_user__in']){
      setSellers([])
      form.setValue('user__hash_user__in', undefined)
    }
    else if(filterValues['user__hash_user__in']?.length > 0){
      const fetchUsers = async () => {
        let filter = {}
        filter.hash_user__in = filterValues['user__hash_user__in'].join(',')

        const allUser = await reservationServices.userReservtionFilter(filter)
        form.setValue('user__hash_user__in', allUser.map(user=>user.hashuser))
        setSellers(allUser)
      }
      fetchUsers()
    }
  },[filterValues['user__hash_user__in'], displayedFilters['user__hash_user__in']])

  useEffect(()=>{
    let auxDisplayedFilters = displayedFilters
    if(initialFields.subtype === 0){
      auxDisplayedFilters.date_in = false
    } else {
      auxDisplayedFilters.item__date_in = false
    }
    setFilters(auxDisplayedFilters)
  },[initialFields.subtype])

  useEffect(()=>{
    if(!displayedFilters['business_unit__salesperson_hash_user__in']){
      setCommercialResponsible([])
      form.setValue('business_unit__salesperson_hash_user__in', undefined)
    }
    else if(filterValues['business_unit__salesperson_hash_user__in']?.length > 0){
      const fetchUsers = async () => {
        let filter = {}
        filter.hash_user__in  = filterValues['business_unit__salesperson_hash_user__in'].join(',')

        const allUser = await reservationServices.userReservtionFilter(filter)
        form.setValue('business_unit__salesperson_hash_user__in', allUser.map(user=>user.hashuser))
        setCommercialResponsible(allUser)
      }
      fetchUsers()
    }
  },[filterValues['business_unit__salesperson_hash_user__in'], displayedFilters['business_unit__salesperson_hash_user__in']])

  useEffect(()=>{
    if(!displayedFilters['business_unit__hash_user__in']){
      setManagerResponsible([])
      form.setValue('business_unit__hash_user__in', undefined)
    }
    else if(filterValues['business_unit__hash_user__in']?.length > 0){
      const fetchUsers = async () => {
        let filter = {}
        filter.hash_user__in  = filterValues['business_unit__hash_user__in'].join(',')

        const allUser = await reservationServices.userReservtionFilter(filter)
        form.setValue('business_unit__hash_user__in', allUser.map(user=>user.hashuser))
        setManagerResponsible(allUser)
      }
      fetchUsers()
    }
  },[filterValues['business_unit__hash_user__in'], displayedFilters['business_unit__hash_user__in']])
  
  useEffect(() => {
    if(reportToEdit){
      Object.entries(filterValues).forEach((entry) => {
        const [key, value] = entry
        if(!form.getValues(key) || (form.getValues(key) && form.getValues(key).length === 0)){
          form.setValue(key, value)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues])

  useEffect(() => {
    if (displayedFilters && Object.keys(displayedFilters).length === 0) {
      form.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedFilters])

  const buildBody = (initialFields) =>{
    let body = {}
    let extra_filters = {}
    let user_to = []
    extra_filters['number__in'] = initialFields.number__in ? 
      initialFields.number__in.replace(/\s/g, '') : ''
    extra_filters['type'] = initialFields.type
    extra_filters['subtype'] = initialFields.subtype
    user_to = initialFields.user_to

    body['extra_filters'] = extra_filters
    body['user_to'] = user_to
    body['name'] = initialFields.name
    
    return body
  }

  const setDayHour = (date, hour) =>{
    if(date.includes('T')){
      return date.split('T')[0] + 'T' + hour
    }
    return date
  }

  let searchTimeout
  const getUserB2 = async (user, length = 2) => {
    clearTimeout(searchTimeout)

    if (
      (!user?.firstname || user.firstname.length < length) &&
      (!user?.surname || user.surname.length < length)
    ) {
      setUserList([])
      return
    }

    searchTimeout = setTimeout(async () => {
      try {
        setLoadingUserList(true)
        let filter = {
          limit: 20
        }
        if (user?.firstname) {
          filter.firstname = user?.firstname
        }
        if (user?.surname) {
          filter.surname = user?.surname
        }
        const allUser = await reservationServices.userReservtionFilter(filter)
        setUserList(allUser)
      } catch (error) {
        setUserList([])
      } finally {
        setLoadingUserList(false)
      }
    }, 1000)
  }

  const decideDateParam = (initialFields) =>{
    let dateObj = {}
    let fromDate = null
    let toDate = null
    if(initialFields.dateFrom){
       fromDate = setDayHour(initialFields.dateFrom.toISOString(), '00:00:00.000Z')
    }
    if(initialFields.dateTo){
      toDate = setDayHour(initialFields.dateTo.toISOString(), '23:59:59.000Z')
    }
    if(initialFields.date_filter_type === 0){
      if(fromDate){
        dateObj.create_at__gte = fromDate
      }
      if(toDate){
        dateObj.create_at__lte = toDate
      }
    }
    else if(initialFields.date_filter_type === 1 && initialFields.subtype === 1){
      if(fromDate){
        dateObj.date_in__gte = fromDate.split('T')[0]
      }
      if(toDate){
        dateObj.date_in__lte = toDate.split('T')[0]
      }
    }
    else if(initialFields.date_filter_type === 1 && initialFields.subtype === 0){
      if(fromDate){
        dateObj.item__date_in__gte = fromDate.split('T')[0]
      }
      if(toDate){
        dateObj.item__date_in__lte = toDate.split('T')[0]
      }
    }
    else if(initialFields.date_filter_type === 2){
      if(fromDate){
        dateObj.item__autocancel_date__gte = fromDate.split('T')[0]
      }
      if(toDate){
        dateObj.item__autocancel_date__lte = toDate.split('T')[0]
      }
    }
    else if(initialFields.date_filter_type === 3){
      if(fromDate){
        dateObj.item__due_date_final__gte = fromDate.split('T')[0]
      }
      if(toDate){
        dateObj.item__due_date_final__lte = toDate.split('T')[0]
      }
    }
    else if(initialFields.date_filter_type === 4){
      if(fromDate){
        dateObj.billing_date__gte = fromDate.split('T')[0]
      }
      if(toDate){
        dateObj.billing_date__lte = toDate.split('T')[0]
      }
    }
    else if(initialFields.date_filter_type === 5){
      if(fromDate){
        dateObj.item__updated_at__gte = fromDate
      }
      if(toDate){
        dateObj.item__updated_at__lte = toDate
      }
    }
    else if(initialFields.date_filter_type === 6){
      if(fromDate){
        dateObj.cancel_info__date__gte = fromDate.split('T')[0]
      }
      if(toDate){
        dateObj.cancel_info__date__lte = toDate.split('T')[0]
      }
    }
    else if(initialFields.date_filter_type === 7){
      if(fromDate){
        dateObj.item__created_at__gte = fromDate
      }
      if(toDate){
        dateObj.item__created_at__lte = toDate
      }
    }
    else if(initialFields.date_filter_type === 8){
      if(fromDate){
        dateObj.item__date_out__gte = fromDate.split('T')[0]
      }
      if(toDate){
        dateObj.item__date_out__lte = toDate.split('T')[0]
      }
    }
    return dateObj
  }

  const validateFields = (initialFields, fields) =>{
    let auxValidation = {}
    let error = false
    for(let field of fields){
      if(initialFields[field] === null || initialFields[field].length === 0){
        auxValidation[field] = true
        error = true
      }
    }
    setFormError(form=>{return {...form, ...auxValidation}})
    return error
  }

  const onDemandValidation = (initialFields) =>{
    if(initialFields.configuration === 1){
      if(validateFields(initialFields, ['type', 'dateFrom', 'dateTo', 'name', 'subtype', 'configuration', 'date_filter_type'])){
        notify('ra.validation.missingFields', {type:'error'})
        return false
      }
      return true
    } else if(initialFields.configuration === 2){
      if(validateFields(initialFields, ['type', 'name', 'subtype', 'configuration', 'number__in'])){
        notify('ra.validation.missingFields', {type:'error'})
        return false
      }
      return true
    }
  }

  const preconfiguredValidation = (initialFields) =>{
    let fieldsToValidate = ['type', 'name', 'subtype', 'configuration', 'date_filter_type', 'date_filter_range', 'type_frequency']
    if(initialFields.type_frequency !== 0){
      fieldsToValidate.push('frequency')
    }
    if(initialFields.date_filter_range === 13){
      fieldsToValidate.push('custom__gte')
      fieldsToValidate.push('custom__lte')
    }
    if(validateFields(initialFields, fieldsToValidate)){
      notify('ra.validation.missingFields', {type:'error'})
      return false
    }
    return true
  }

  const onSubmit = async () => {
    let formValues = form.getValues()
    if (Object.keys(formValues).length > 0) {
      if (form.getValues().business_unit__country_id__in) {
        formValues.business_unit__country_id__in = form.getValues().business_unit__country_id__in.map((elem) => elem.id)
        form.setValue('business_unit__country_id__in', formValues.business_unit__country_id__in)
        
      }
      if (formValues.user__hash_user__in) {
        formValues.user__hash_user__in = formValues.user__hash_user__in.map(user=>user.hashuser)
      }
      if (formValues.business_unit__salesperson_hash_user__in) {
        formValues.business_unit__salesperson_hash_user__in = formValues.business_unit__salesperson_hash_user__in.map(user=>user.hashuser)
      }
      if (formValues.business_unit__hash_user__in) {
        formValues.business_unit__hash_user__in = formValues.business_unit__hash_user__in.map(user=>user.hashuser)
      }
      if(initialFields.subtype === 0){
        delete formValues.date_in__gte
        delete formValues.date_in__lte
      } else {
        delete formValues.item__date_in__gte
        delete formValues.item__date_in__lte
      }
    }
    if(initialFields.configuration === 1 || initialFields.configuration === 2){
      if(!onDemandValidation(initialFields)){
        return
      }
      const body = buildBody(initialFields)
      const dateFilter = initialFields.configuration === 1 ? decideDateParam(initialFields) : {}
      initialFields.configuration = null
      setCreateLoading(true)
      const formData = new FormData();
      formData.append("name", body.name);
      formData.append("extra_filters", JSON.stringify(body.extra_filters));
      formData.append("user_to", JSON.stringify(body.user_to));
      await reportingServices.buildOnDemandReport({...formValues, ...dateFilter}, formData).then(()=>{
        notify('ra.message.create_report_success', {type:'success'})
        setActivateRefresh(true)
        navigate(-1)
      }).catch(()=>{
        notify('ra.message.create_report_error', {type:'error'})
      }).finally(()=>{
        setCreateLoading(false)
      })
    } else {
      if(!preconfiguredValidation(initialFields)){
        return
      }
      initialFields.configuration = null
      let dateFilter = {
        custom__gte:initialFields.custom__gte,
        custom__lte:initialFields.custom__lte,
      }
      initialFields.custom__gte = null
      initialFields.custom__lte = null
      if(form.getValues('pax_item__ticket_number__in')){
        formValues.pax_item__ticket_number__in = 
          Array.isArray(form.getValues('pax_item__ticket_number__in')) ?  
          form.getValues('pax_item__ticket_number__in') :
          form.getValues('pax_item__ticket_number__in').split(',')
        form.setValue('pax_item__ticket_number__in', formValues.pax_item__ticket_number__in)
      }
      const body = {...initialFields, filters:{...formValues, ...dateFilter}}
      if(reportToEdit){
        setEditLoading(true)
        await reportingServices.editReport(reportToEdit.id, {...reportToEdit, ...body}).then(()=>{
          setOpenEditDialog(false)
          notify('ra.message.edit_report_success', {type:'success'})
          setRefresh(value=>!value)
        }).catch(()=>{
          notify('ra.message.edit_report_error', {type:'error'})
        }).finally(()=>{
          setEditLoading(true)
        })
      } else {
        setCreateLoading(true)
        await reportingServices.buildPreconfiguredReport(body).then(()=>{
          notify('ra.message.create_report_success', {type:'success'})
          setRefresh(value=>!value)
          navigate(-1)
        }).catch(()=>{
          notify('ra.message.create_report_error', {type:'error'})
        }).finally(()=>{
          setCreateLoading(false)
        })
      }
    }
    
  }

  function convertToUTC(dateString) {
    return dateString
  }

  function convertFromUTC(dateString) {
    return dateString
  }

  return (
    <FormProvider {...form}>
      <form>
        <Box display='flex' alignItems='center' mb={1} height={'auto'} width={'60%'}>
          <Box width={'calc(100% - 156px)'} display={'flex'} flexDirection={'column'} flexWrap={'wrap'}>
            {displayedFilters?.create_at && (
              <RemoveFilterField isShow={isShow} prop={'create_at'} fields={['create_at__gte', 'create_at__lte']} width={500} displayedFilters = {displayedFilters} setFilters={setFilters}>
                <DateTimeInput
                  label={translate('ra.field.start_create_date')}
                  fullWidth
                  disabled={isShow}
                  source='create_at__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateTimeInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.end_create_date')}
                  fullWidth
                  disabled={isShow}
                  source='create_at__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.number__ilike_in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'number__ilike_in'} fields={['number__ilike_in']} width={300}>
                <TextInput
                  label={translate('ra.field.reservation_number')}
                  fullWidth
                  disabled={isShow}
                  source='number__ilike_in'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.work_unit_id__in && (
              <RemoveFilterField isShow={isShow}
                displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'work_unit_id__in'}
                fields={['work_unit_id__in']}
                minWidth={300}
                maxWidth={600}
              >
                <TreeViewInput
                  isShow = {isShow}
                  queryName='work-unit-all'
                  fieldName='work_unit_id__in'
                  modalTitle={translate('ra.title.select_work_unit')}
                  buttonText={translate('ra.message.filter_by_work_unit')}
                  labelField={['label']}
                  queryFn={workUnitServices.getByOrderParent}
                  filterValues={filterValues}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.business_unit_id__in && (
              <RemoveFilterField isShow={isShow}
                displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'business_unit_id__in'}
                fields={['business_unit_id__in']}
                minWidth={300}
                maxWidth={600}
              >
                <TreeViewInput
                  isShow={isShow}
                  queryName='business-unit-all'
                  fieldName='business_unit_id__in'
                  modalTitle='Selecciona una unidad de negocio'
                  buttonText={translate('ra.message.filter_by_business_unit')}
                  labelField={['fiscal_data_relation.name', 'fantasy_name']}
                  queryFn={businessUnitServices.getByOrderParent}
                  filterValues={filterValues}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.business_unit__country_id__in && (
              <RemoveFilterField isShow={isShow}
                displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'business_unit__country_id__in'}
                fields={['business_unit__country_id__in']}
                minWidth={300}
                maxWidth={600}
              >
                <CountryInput isShow={isShow} label={translate('ra.field.country')} source='business_unit__country_id__in' />
              </RemoveFilterField>
            )}

            <Dialog open={openSellerDialog} maxWidth='md' fullWidth onClose={()=>setOpenSellerDialog(false)}>
              <DialogTitle display='flex' justifyContent='space-between'>
                {translate('ra.field.seller')}
                <IconButton size='small' onClick={()=>setOpenSellerDialog(false)}>
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <MultiAutocompleteInput
                    source='user__hash_user__in'
                    options={usersList}
                    loading={usersLoading}
                    multiple
                    onChange={val=>{setSellers(val.target.value)}}
                    optionsText={['firstname', 'surname']}
                    onSearch={getUserB2}
                    resetOnLoad={false}
                    fields={['firstname', 'surname']}
                    disablePortal={false}
                  />
              </DialogContent>
            </Dialog>
            <Dialog open={openCommercialResponsibleDialog} maxWidth='md' fullWidth onClose={()=>setOpenCommercialResponsibleDialog(false)}>
              <DialogTitle display='flex' justifyContent='space-between'>
                {translate('ra.field.commercial_responsible')}
                <IconButton size='small' onClick={()=>setOpenCommercialResponsibleDialog(false)}>
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <MultiAutocompleteInput
                    source='business_unit__salesperson_hash_user__in'
                    options={usersList}
                    loading={usersLoading}
                    onChange={val=>{setCommercialResponsible(val.target.value)}}
                    multiple
                    optionsText={['firstname', 'surname']}
                    onSearch={getUserB2}
                    resetOnLoad={false}
                    fields={['firstname', 'surname']}
                    disablePortal={false}
                  />
              </DialogContent>
            </Dialog>
            <Dialog open={openManagerResponsibleDialog} maxWidth='md' fullWidth onClose={()=>setOpenManagerResponsibleDialog(false)}>
              <DialogTitle display='flex' justifyContent='space-between'>
                {translate('ra.field.manager_responsible')}
                <IconButton size='small' onClick={()=>setOpenManagerResponsibleDialog(false)}>
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <MultiAutocompleteInput
                    source='business_unit__hash_user__in'
                    options={usersList}
                    loading={usersLoading}
                    onChange={val=>{setManagerResponsible(val.target.value)}}
                    multiple
                    optionsText={['firstname', 'surname']}
                    onSearch={getUserB2}
                    resetOnLoad={false}
                    fields={['firstname', 'surname']}
                    disablePortal={false}
                  />
              </DialogContent>
            </Dialog>
            {displayedFilters?.user__hash_user__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'user__hash_user__in'} fields={['user__hash_user__in']} width={300}>
                <div>
                  <Box width={'100%'}>
                    <Button
                      sx={{
                        width: '100%',
                        height: '40px',
                      }}
                      onClick={()=>setOpenSellerDialog(true)}
                      variant='outlined'
                      style={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}
                    >
                      <span style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflowX: 'hidden' }}>
                        {
                          
                          ((reportToEdit && sellers && sellers.length > 0) ||
                          (!reportToEdit && form.getValues('user__hash_user__in') && form.getValues('user__hash_user__in').length > 0)) ?
                          sellers.map(user=>(
                            user.firstname + ' ' + user.surname
                          )).join(',')
                          :
                          translate('ra.message.filter_by_seller')
                        }
                      </span>
                    </Button>
                  </Box>
                </div>
              </RemoveFilterField>
            )}
            {/* {displayedFilters?.business_unit__user_id__in && (
              <RemoveFilterField isShow={isShow}
                displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'business_unit__user_id__in'}
                fields={['business_unit__user_id__in']}
                width={300}
              >
                <SelectArrayInputFn
                  source='business_unit__user_id__in'
                  queryName='users-all'
                  disabled={isShow}
                  label={translate('ra.field.manager_responsible')}
                  queryFn={userServices.getByAll}
                  optionsText={['firstname', 'surname']}
                  separator=' '
                />
              </RemoveFilterField>
            )} */}
            {displayedFilters?.business_unit__hash_user__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'business_unit__hash_user__in'} fields={['business_unit__hash_user__in']} width={300}>
                <div>
                  <Box width={'100%'}>
                    <Button
                      sx={{
                        width: '100%',
                        height: '40px',
                      }}
                      onClick={()=>setOpenManagerResponsibleDialog(true)}
                      variant='outlined'
                      style={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}
                    >
                      <span style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflowX: 'hidden' }}>
                        {
                          
                          ((reportToEdit && managerResponsible&& managerResponsible.length > 0) ||
                          (!reportToEdit && form.getValues('business_unit__hash_user__in') && form.getValues('business_unit__hash_user__in').length > 0)) ?
                          managerResponsible.map(user=>(
                            user.firstname + ' ' + user.surname
                          )).join(',')
                          :
                          translate('ra.field.manager_responsible')
                        }
                      </span>
                    </Button>
                  </Box>
                </div>
              </RemoveFilterField>
            )}
            {displayedFilters?.business_unit__salesperson_hash_user__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'business_unit__salesperson_hash_user__in'} fields={['business_unit__salesperson_hash_user__in']} width={300}>
                <div>
                  <Box width={'100%'}>
                    <Button
                      sx={{
                        width: '100%',
                        height: '40px',
                      }}
                      onClick={()=>setOpenCommercialResponsibleDialog(true)}
                      variant='outlined'
                      style={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}
                    >
                      <span style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflowX: 'hidden' }}>
                        {
                          
                          ((reportToEdit && commercialResponsible && commercialResponsible.length > 0) ||
                          (!reportToEdit && form.getValues('business_unit__salesperson_hash_user__in') && form.getValues('business_unit__salesperson_hash_user__in').length > 0)) ?
                          commercialResponsible.map(user=>(
                            user.firstname + ' ' + user.surname
                          )).join(',')
                          :
                          translate('ra.field.commercial_responsible')
                        }
                      </span>
                    </Button>
                  </Box>
                </div>
              </RemoveFilterField>
            )}
            {displayedFilters?.billing_base__in && (
              <RemoveFilterField isShow={isShow}
                displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'billing_base__in'}
                fields={['billing_base__in']}
                width={300}
              >
                <SelectArrayInputFn
                  source='billing_base__in'
                  queryName='bases-all'
                  disabled={isShow}
                  optionValue={['name']}
                  label={translate('ra.field.billing_base')}
                  queryFn={reservationServices.getAllAlternativeBases}
                  optionsText={['name']}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.payment_type__in && (
              <RemoveFilterField isShow={isShow}
                displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'payment_type__in'}
                fields={['payment_type__in']}
                width={300}
              >
                <SelectArrayInputFn
                  source='payment_type__in'
                  queryName='payment-type'
                  disabled={isShow}
                  optionValue={['id']}
                  label={translate('ra.field.payment_type')}
                  queryFn={paymentServices.getAllPaymentTypes}
                  optionsText={['name']}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.status__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'status__in'} fields={['status__in']} width={300}>
                <FormControl fullWidth className='select-array-input'>
                  <InputLabel>{translate('ra.field.reservation_status')}</InputLabel>
                  <SelectInput
                    disabled={isShow}
                    source='status__in'
                    options={[
                      { id: 'OPEN', name: translate('ra.status.OPEN') },
                      { id: 'FINALIZED', name: translate('ra.status.FINALIZED') },
                      { id: 'CLOSED', name: translate('ra.status.CLOSED') },
                      { id: 'CANCELLED', name: translate('ra.status.CANCELLED') },
                      { id: 'DELETED', name: translate('ra.status.DELETED') }
                    ]}
                    optionsText={['name']}
                    multiple
                  />
                </FormControl>
              </RemoveFilterField>
            )}
            {displayedFilters?.date_in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'date_in'} fields={['date_in__gte', 'date_in__lte']} width={500}>
                <DateInput
                  label={translate('ra.field.start_date_first_service')}
                  fullWidth
                  disabled={isShow}
                  source='date_in__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateInput
                  label={translate('ra.field.end_date_first_service')}
                  fullWidth
                  disabled={isShow}
                  source='date_in__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                  sx={{ ml: 5 }}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__date_in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'item__date_in'} fields={['item__date_in__gte', 'item__date_in__lte']} width={500}>
                <DateInput
                  label={translate('ra.field.start_date_first_service')}
                  fullWidth
                  disabled={isShow}
                  source='item__date_in__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateInput
                  label={translate('ra.field.end_date_first_service')}
                  fullWidth
                  disabled={isShow}
                  source='item__date_in__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                  sx={{ ml: 5 }}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.pax && (
              <RemoveFilterField isShow={isShow}
              displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'pax'}
                fields={['pax__name__ilike', 'pax__surname__ilike']}
                width={500}
              >
                <TextInput disabled={isShow} label={translate('ra.field.name')} fullWidth source='pax__name__ilike' />
                <TextInput
                  sx={{ ml: 5 }}
                  disabled={isShow}
                  label={translate('ra.field.surname')}
                  fullWidth
                  source='pax__surname__ilike'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.pax_item__ticket_number__in && (
              <RemoveFilterField isShow={isShow}
              displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'pax_item__ticket_number__in'}
                fields={['pax_item__ticket_number__in']}
                width={500}
              >
                <TextInput
                  sx={{ ml: 5 }}
                  disabled={isShow}
                  parse={(value) => value?.trim()}
                  label={translate('ra.field.item_ticket_number')}
                  fullWidth
                  source='pax_item__ticket_number__in'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.product_type_id__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'product_type_id__in'} fields={['product_type_id__in']} width={300}>
                <SelectArrayInputFn
                  disabled={isShow}
                  source='product_type_id__in'
                  queryName='product-type-all'
                  label={translate('ra.field.product_type')}
                  queryFn={() => productTypeServices.getByParam({ limit: 999 })}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__product_id__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'item__product_id__in'} fields={['item__product_id__in']} width={300}>
                <SelectArrayInputFn
                  disabled={isShow}
                  source='item__product_id__in'
                  queryName='product-all'
                  label={translate('ra.field.product')}
                  queryFn={productServices.getByAll}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__source_id__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'item__source_id__in'} fields={['item__source_id__in']} width={300}>
                <SelectArrayInputFn
                  source='item__source_id__in'
                  disabled={isShow}
                  queryName='source-all'
                  label={translate('ra.field.source')}
                  queryFn={sourceServices.getByAll}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__due_date_final && (
              <RemoveFilterField isShow={isShow}
              displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'item__due_date_final'}
                fields={['item__due_date_final__gte', 'item__due_date_final__lte']}
                width={500}
              >
                <DateInput
                  disabled={isShow}
                  label={translate('ra.field.start_due_date')}
                  fullWidth
                  source='item__due_date_final__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateInput
                  disabled={isShow}
                  label={translate('ra.field.end_due_date')}
                  fullWidth
                  source='item__due_date_final__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                  sx={{ ml: 5 }}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__status__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'item__status__in'} fields={['item__status__in']} width={300}>
                <FormControl fullWidth className='select-array-input'>
                  <InputLabel>{translate('ra.field.item_status')}</InputLabel>
                  <SelectInput
                    source='item__status__in'
                    disabled={isShow}
                    options={[
                      { id: 'CONFIRMED', name: translate('ra.status.CONFIRMED') },
                      { id: 'REJECTED', name: translate('ra.status.REJECTED_MALE') },
                      { id: 'CANCELLED', name: translate('ra.status.CANCELLED') },
                      { id: 'PENDING', name: translate('ra.status.PENDING') },
                      { id: 'INVOICED', name: translate('ra.status.INVOICED') },
                      { id: 'REFUNDED', name: translate('ra.status.REFUNDED') },
                      { id: 'PARTIAL_REFUND', name: translate('ra.status.PARTIAL_REFUND') },
                      { id: 'EXPIRED', name: translate('ra.status.EXPIRED_MALE') },
                      { id: 'REINVOICED', name: translate('ra.status.REINVOICED') },
                      { id: 'VOID', name: translate('ra.status.VOID') },
                      { id: 'ERROR', name: translate('ra.status.ERROR') },
                      { id: 'UNDEFINED', name: translate('ra.status.UNDEFINED') },
                      { id: 'PENDING_RESERVATION', name: translate('ra.status.PENDING_RESERVATION') },
                      { id: 'INVOICE_IN_PROGRESS', name: translate('ra.status.INVOICE_IN_PROGRESS') },
                      { id: 'CANCELLATION_IN_PROGRESS', name: translate('ra.status.CANCELLATION_IN_PROGRESS') },
                      { id: 'VOID_IN_PROGRESS', name: translate('ra.status.VOID_IN_PROGRESS') },
                      { id: 'FAILED_INVOICED', name: translate('ra.status.FAILED_INVOICED') },
                      { id: 'AFTER_SALES', name: translate('ra.status.AFTER_SALES') },
                    ]}
                    optionsText={['name']}
                    multiple
                  />
                </FormControl>
              </RemoveFilterField>
            )}
            {displayedFilters?.item__payment_status__in && (
              <RemoveFilterField isShow={isShow}
              displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'item__payment_status__in'}
                fields={['item__payment_status__in']}
                width={400}
              >
                <FormControl fullWidth className='select-array-input'>
                  <InputLabel>{translate('ra.field.payment_status')}</InputLabel>
                  <SelectInput
                    disabled={isShow}
                    source='item__payment_status__in'
                    options={[
                      { id: 'GUARANTEED', name: translate('ra.status.GUARANTEED') },
                      { id: 'UNPAID', name: translate('ra.status.UNPAID') },
                      { id: 'PAID', name: translate('ra.status.PAID') },
                      { id: 'PARTIALLY_PAID', name: translate('ra.status.PARTIALLY_PAID') },
                      { id: 'NOT_REQUIRED', name: translate('ra.status.NOT_REQUIRED') },
                      { id: 'IN_REVIEW', name: translate('ra.status.IN_REVIEW') },
                      { id: 'CREDIT_GENERATED', name: translate('ra.status.CREDIT_GENERATED') },
                      { id: 'CREDIT_NOT_GENERATED', name: translate('ra.status.CREDIT_NOT_GENERATED') },
                    ]}
                    optionsText={['name']}
                    multiple
                  />
                </FormControl>
              </RemoveFilterField>
            )}
            {displayedFilters?.item_billing_status__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'item_billing_status__in'} fields={['item_billing_status__in']} width={300}>
                <FormControl fullWidth className='select-array-input'>
                  <InputLabel>{translate('ra.field.item_billing_status')}</InputLabel>
                  <SelectInput
                    disabled={isShow}
                    source='item_billing_status__in'
                    options={[
                      { id: 'PENDING', name: translate('ra.status.PENDING_BILLING') },
                      { id: 'NOT_REQUIRED', name: translate('ra.status.NOT_REQUIRED') },
                      { id: 'BILLED', name: translate('ra.status.BILLED') },
                      { id: 'PARTIALLY_BILLED', name: translate('ra.status.TO_BILLING') },
                    ]}
                    optionsText={['name']}
                    multiple
                  />
                </FormControl>
              </RemoveFilterField>
            )}
            {displayedFilters?.supplier_id__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'supplier_id__in'} fields={['supplier_id__in']} width={300}>
                <SelectArrayInputFn
                  disabled={isShow}
                  source='supplier_id__in'
                  queryName='supplier-all'
                  label={translate('ra.field.supplier')}
                  queryFn={() => supplierServices.getByParam({ limit: 999 })}
                  optionsText={['name']}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.provider_code__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'provider_code__in'} fields={['provider_code__in']} width={300}>
                <TextInput disabled={isShow} label={translate('ra.field.provider_code')} fullWidth source='provider_code__in' />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__autocancel_date && (
              <RemoveFilterField isShow={isShow}
              displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'item__autocancel_date'}
                fields={['item__autocancel_date__gte', 'item__autocancel_date__lte']}
                width={500}
              >
                <DateInput
                  disabled={isShow}
                  label={translate('ra.field.start_autocancel_date')}
                  fullWidth
                  source='item__autocancel_date__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateInput
                  sx={{ ml: 5 }}
                  disabled={isShow}
                  label={translate('ra.field.end_autocancel_date')}
                  fullWidth
                  source='item__autocancel_date__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.cancel_info__date && (
              <RemoveFilterField isShow={isShow}
              displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'cancel_info__date'}
                fields={['cancel_info__date__lte', 'cancel_info__date__gte']}
                width={500}
              >
                <DateTimeInput
                  disabled={isShow}
                  label={`${translate('ra.field.supplier_due_date')} ${translate('ra.field.from')}`}
                  fullWidth
                  source='cancel_info__date__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateTimeInput
                  sx={{ ml: 5 }}
                  label={`${translate('ra.field.supplier_due_date')} ${translate('ra.field.to')}`}
                  fullWidth
                  disabled={isShow}
                  source='cancel_info__date__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__created_at && (
              <RemoveFilterField isShow={isShow}
              displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'item__created_at'}
                fields={['item__created_at__lte', 'item__created_at__gte']}
                width={500}
              >
                <DateTimeInput
                  disabled={isShow}
                  label={`${translate('ra.field.item_create_date')} ${translate('ra.field.from')}`}
                  fullWidth
                  source='item__created_at__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateTimeInput
                  sx={{ ml: 5 }}
                  label={`${translate('ra.field.item_create_date')} ${translate('ra.field.to')}`}
                  fullWidth
                  disabled={isShow}
                  source='item__created_at__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__updated_at && (
              <RemoveFilterField isShow={isShow}
              displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'item__updated_at'}
                fields={['item__updated_at__lte', 'item__updated_at__gte']}
                width={500}
              >
                <DateTimeInput
                  disabled={isShow}
                  label={translate('ra.field.start_last_modification_date')}
                  fullWidth
                  source='item__updated_at__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateTimeInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.end_last_modification_date')}
                  fullWidth
                  disabled={isShow}
                  source='item__updated_at__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.tag__id__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'tag__id__in'} fields={['tag__id__in']} width={500}>
                <SelectArrayInputFn
                  source='tag__id__in'
                  disabled={isShow}
                  queryName='tag-all'
                  label={translate('ra.field.tags')}
                  queryFn={() => tagServices.getTagsWithParams({ limit: 999 })}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.sales_id__in && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'sales_id__in'} fields={['sales_id__in']} width={300}>
                <TextInput disabled={isShow} label={translate('ra.field.tma_business')} fullWidth source='sales_id__in' />
              </RemoveFilterField>
            )}
            {displayedFilters?.billing_status && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'billing_status'} fields={['billing_status']} width={300}>
                <BooleanInput
                  label={translate('ra.field.billing_status')}
                  fullWidth
                  disabled={isShow}
                  source='billing_status'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.billing_automatic && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'billing_automatic'} fields={['billing_automatic']} width={300}>
                <BooleanInput
                  disabled={isShow}
                  label={translate('ra.field.billing_automatic')}
                  fullWidth
                  source='billing_automatic'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__is_billable && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'item__is_billable'} fields={['item__is_billable']} width={300}>
                <BooleanInput
                  label={translate('ra.field.is_billable')}
                  fullWidth
                  disabled={isShow}
                  source='item__is_billable'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__has_smartbook && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'item__has_smartbook'} fields={['item__has_smartbook']} width={200}>
                <BooleanInput
                  label={translate('ra.field.smartbookTitle')}
                  fullWidth
                  disabled={isShow}
                  source='item__has_smartbook'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__has_rebooker && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'item__has_rebooker'} fields={['item__has_rebooker']} width={200}>
                <BooleanInput
                  label={translate('ra.field.rebooker')}
                  fullWidth
                  disabled={isShow}
                  source='item__has_rebooker'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.billing_has_receipt && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'billing_has_receipt'} fields={['billing_has_receipt']} width={200}>
                <BooleanInput
                  label={translate('ra.field.receipt_generated')}
                  fullWidth
                  disabled={isShow}
                  source='billing_has_receipt'
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__has_flexible_rate && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop={'item__has_flexible_rate'} fields={['item__has_flexible_rate']} width={200}>
                <BooleanInput
                  disabled={isShow}
                  label={translate('ra.field.flexi')}
                  fullWidth
                  source='item__has_flexible_rate'
                />
              </RemoveFilterField>
            )}
            {displayedFilters.item__has_ancillary_baggage && (
              <RemoveFilterField isShow={isShow} displayedFilters = {displayedFilters} setFilters={setFilters} prop="item__has_ancillary_baggage" fields={['item__has_ancillary_baggage']}>
                <BooleanInput
                  disabled={isShow}
                  source="item__has_ancillary_baggage"
                  label={translate('ra.title.extra_baggage')}
                  fullWidth
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.billing_date && (
              <RemoveFilterField isShow={isShow}
              displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'billing_date'}
                disabled={isShow}
                fields={['billing_date__gte', 'billing_date__lte']}
                width={500}
              >
                <DateInput
                  label={translate('ra.field.start_billing_date')}
                  fullWidth
                  disabled={isShow}
                  source='billing_date__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.end_billing_date')}
                  fullWidth
                  disabled={isShow}
                  source='billing_date__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.item__date_out && (
              <RemoveFilterField isShow={isShow}
              displayedFilters = {displayedFilters} setFilters={setFilters}
                prop={'item__date_out'}
                disabled={isShow}
                fields={['item__date_out__gte', 'item__date_out__lte']}
                width={500}
              >
                <DateInput
                  label={translate('ra.field.end_date')}
                  fullWidth
                  disabled={isShow}
                  source='item__date_out__gte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
                <DateInput
                  sx={{ ml: 5 }}
                  label={translate('ra.field.end_date')}
                  fullWidth
                  disabled={isShow}
                  source='item__date_out__lte'
                  format={convertFromUTC}
                  parse={convertToUTC}
                />
              </RemoveFilterField>
            )}
          </Box>
          <Box
            width={'156px'}
            display={'flex'}
            flexWrap={'wrap'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
          >
          </Box>
        </Box>
      </form>
      {reportToEdit ?
      isShow ?
      <div>
        <Button variant='contained' onClick={()=>setOpenEditDialog(false)}>
          {translate('ra.action.close')}
        </Button>
      </div>
      :
      <div style={{width:'100%', display:'flex', flexDirection:'row', justifyContent:'space-between'}}>
        <LoadingButton loading={editLoading} variant='contained' onClick={onSubmit}>
          {translate('ra.action.save')}
        </LoadingButton>
        <Button variant='outlined' onClick={()=>setOpenEditDialog(false)}>
          {translate('ra.action.cancel')}
        </Button>
      </div>
      :
        <div style={{width:'100%', display:'flex', flexDirection:'row'}}>
          <LoadingButton style={{marginRight:'15px'}} loading={createLoading} variant='contained' onClick={onSubmit}>
              {translate('ra.action.generate')}
          </LoadingButton>
          <Button
            variant='outlined'
            color='error'
            onClick={()=>navigate(-1)}
          >
            {translate('ra.action.cancel')}
          </Button>
        </div>
      }
    </FormProvider>
  )
}
